import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardActionArea, CardContent, CircularProgress,
} from '@mui/material';
import { listMyOrders, CustomerOrderListItem } from '../../api/publicOrders';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorRetry } from '../../components/ui/ErrorRetry';
import { StatusChip } from '../../components/ui/StatusChip';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-DO', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTotal(amount: string): string {
  return `RD$ ${Number(amount).toFixed(2)}`;
}

function formatItemCount(n: number): string {
  return n === 1 ? '1 producto' : `${n} productos`;
}

export const MyOrders: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['my-orders'],
    queryFn: listMyOrders,
    retry: false,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress sx={{ color: 'text.headline' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', mt: { xs: 2, sm: 4 }, px: 3, pb: 6 }}>
        <PageHeader title="Mis pedidos" />
        <ErrorRetry message="Error al cargar tus pedidos." onRetry={() => refetch()} />
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box sx={{ maxWidth: 480, mx: 'auto', mt: 6, px: 3 }}>
        <EmptyState
          title="Aún no tienes pedidos"
          body="Cuando hagas tu primer pedido, lo verás aquí."
          cta={{ label: 'Buscar medicamentos', to: '/buscar' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: { xs: 2, sm: 4 }, px: 3, pb: 6 }}>
      <PageHeader title="Mis pedidos" />
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {data.map((order: CustomerOrderListItem, idx: number) => (
          <Card
            key={order.trackingUUID}
            sx={{
              mb: 1.5,
              animation: 'customerStaggerIn 240ms ease-out both',
              animationDelay: `${Math.min(idx, 12) * 40}ms`,
            }}
          >
            <CardActionArea onClick={() => navigate(`/mis-pedidos/${order.trackingUUID}`)}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography sx={{ fontWeight: 500, color: 'text.headline' }}>
                    {formatDate(order.createdAt)}
                  </Typography>
                  <StatusChip status={order.status} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {formatItemCount(order.itemCount)}
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: 'primary.main' }}>
                    {formatTotal(order.totalAmount)}
                  </Typography>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};
